import { useMemo, useState } from "react";
import { Building2, Link2, Plus, Upload } from "lucide-react";
import { Button } from "../../client/components/ui/button";
import useTranslation from "../../client/hooks/useTranslation";
import PropFirmImportModal from "../../prop-firm-manager/PropFirmImportModal";
import { isActivePropFirmAccount } from "../../prop-firm-manager/pfmPeriod";
import { useLiteData } from "../data/LiteDataProvider";
import { periodStart } from "../lib/filters";
import type { Period } from "../lib/types";
import { EmptyStart } from "../onboarding/EmptyStart";
import { PageBody, PageHeader } from "../shell/LiteLayout";
import { useLiteActions } from "../shell/LiteActions";
import { CashflowChart } from "../ui/Charts";
import { Card, Stat, cardCls } from "../ui/ui";
import { EventCalendar } from "./pfm/EventCalendar";
import { ExpenseDialog } from "./pfm/ExpenseDialog";
import { MonthRecap } from "./pfm/MonthRecap";
import { ActivityCard, ExpenseBreakdown, UpcomingCard } from "./pfm/Panels";
import { PeriodTable } from "./pfm/PeriodTable";
import { AccountTable, FirmTable, type FirmRow } from "./pfm/Tables";
import { WalletCard } from "./pfm/WalletCard";
import {
  breakdown, buildEvents, kpis, matchesPhase, monthlyCashflow, passRate, roiOf, scopeCharges,
  type Phase, type PfmAccount,
} from "./pfm/pfmModel";
import { Legend, roiLabel } from "./pfm/shared";

export default function LitePropFirmsPage() {
  const t = useTranslation().lite;
  const p = t.pfm;
  const { loading, fmt, accounts, accountsById, rawAccounts, charges, tptWallet, filters, accountSet, inScope, refetchAll } = useLiteData();
  const { openAddAccount, openConnectBroker, openAddPayout } = useLiteActions();
  const [importOpen, setImportOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);

  const all = rawAccounts as PfmAccount[];
  // Périmètre « qui » (prop firm + compte), puis la phase. Sans filtre : tous les
  // comptes, exactement comme le PFM Advanced.
  // La phase vient du filtre « Compte » commun (Challenges / Funded uniquement).
  const phase: Phase = accountSet === "challenge" || accountSet === "funded" ? accountSet : "all";
  // Périmètre sans la phase (le taux de réussite a besoin des challenges ET des funded), puis la phase.
  const whoScope = useMemo(() => all.filter(a => {
    const la = accountsById.get(a.id);
    return !!la && inScope(la, { ignorePhase: true });
  }), [all, accountsById, inScope]);
  const scope = useMemo(() => whoScope.filter(a => matchesPhase(a, phase)), [whoScope, phase]);
  const scopeIds = useMemo(() => new Set(scope.map(a => a.id)), [scope]);
  const sCharges = useMemo(
    () => scopeCharges(charges, scopeIds, { accountId: filters.accountId, firm: filters.firm, phase }),
    [charges, scopeIds, filters.accountId, filters.firm, phase],
  );
  const predecessors = useMemo(() => new Set(all.map(a => a.previousAccountId).filter((x): x is string => !!x)), [all]);
  const breachedIds = useMemo(() => new Set(accounts.filter(a => a.status === "breached").map(a => a.id)), [accounts]);

  const k = useMemo(() => {
    const from = periodStart(filters.period);
    const to = filters.period === "all" ? null : new Date();
    return { ...kpis(scope, sCharges, from, to), payoutsCount: breakdown(scope, sCharges, from, to).payoutsCount };
  }, [scope, sCharges, filters.period]);
  const active = scope.filter(isActivePropFirmAccount);
  const activeChallenges = active.filter(a => a.status === "challenge").length;
  const pr = useMemo(() => passRate(whoScope, predecessors), [whoScope, predecessors]);

  const monthly = useMemo(() => monthlyCashflow(scope, sCharges, d => fmt.monthShort(d)), [scope, sCharges, fmt]);
  const events = useMemo(() => buildEvents(scope, sCharges, breachedIds), [scope, sCharges, breachedIds]);
  const recent = useMemo(() => {
    const now = Date.now();
    return events.filter(e => e.date.getTime() <= now).slice(0, 8);
  }, [events]);
  const allTime = useMemo(() => breakdown(scope, sCharges, null, null), [scope, sCharges]);

  // Par prop firm, depuis le début : charges du compte uniquement (une charge
  // globale n'appartient à aucune prop firm, elle est signalée à part).
  const firmRows = useMemo<FirmRow[]>(() => {
    const groups = new Map<string, PfmAccount[]>();
    for (const a of scope) {
      const key = accountsById.get(a.id)?.firm?.key;
      if (key) groups.set(key, [...(groups.get(key) ?? []), a]);
    }
    return [...groups.entries()].map(([key, accs]) => {
      const ids = new Set(accs.map(a => a.id));
      const fk = kpis(accs, charges.filter(c => c.accountId && ids.has(c.accountId)), null, null);
      return {
        firm: accountsById.get(accs[0].id)!.firm!,
        total: accs.length,
        active: accs.filter(isActivePropFirmAccount).length,
        passRate: passRate(whoScope.filter(a => accountsById.get(a.id)?.firm?.key === key), predecessors).rate,
        spent: fk.totalExpenses,
        payouts: fk.totalPayouts,
        net: fk.netProfit,
        roi: roiOf(fk.totalPayouts, fk.totalExpenses),
      };
    }).sort((a, b) => b.spent + b.payouts - (a.spent + a.payouts));
  }, [scope, whoScope, charges, accountsById, predecessors]);
  const globalChargesTotal = useMemo(
    () => breakdown([], sCharges.filter(c => !c.accountId), null, null).total,
    [sCharges],
  );

  const tracked = useMemo(() => scope
    .map(a => accountsById.get(a.id)!)
    .filter(a => a && a.firm)
    .sort((a, b) => Number(!a.active) - Number(!b.active) || a.name.localeCompare(b.name)), [scope, accountsById]);

  const periodLabel: Record<Period, string> = {
    today: t.periods.today, week: t.periods.week, month: t.periods.month,
    "3m": t.periods.threeMonths, "6m": t.periods.sixMonths, all: t.periods.all,
  };
  const hasProp = accounts.some(a => a.firm);

  return (
    <>
      <PageHeader
        title={p.title}
        subtitle={p.subtitle}
        filters={hasProp ? "full" : false}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} aria-label={p.actions.import}><Upload className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{p.actions.import}</span></Button>
            <Button variant="outline" onClick={() => setExpenseOpen(true)} aria-label={p.actions.expense}><Plus className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{p.actions.expense}</span></Button>
            <Button onClick={openAddPayout} aria-label={p.actions.payout}><Plus className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{p.actions.payout}</span></Button>
          </div>
        }
      />
      <PageBody>
        {loading ? (
          <div className={`${cardCls} py-16 text-center text-sm text-muted-foreground`}>{t.common.loading}</div>
        ) : !hasProp ? (
          <EmptyStart
            icon={Building2}
            title={p.empty.title}
            text={p.empty.text}
            cta={<>
              <Button onClick={openAddAccount}><Plus className="h-4 w-4 mr-1.5" />{p.empty.addAccount}</Button>
              <Button variant="outline" onClick={openConnectBroker}><Link2 className="h-4 w-4 mr-1.5" />{p.empty.connect}</Button>
            </>}
          />
        ) : (<>

          <div className={`${cardCls} p-5 grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-6`}>
            <Stat label={p.kpi.payouts} value={fmt.money(k.totalPayouts)} tone={k.totalPayouts ? "gain" : undefined} hint={p.kpi.payoutsHint(k.payoutsCount, periodLabel[filters.period].toLowerCase())} />
            <Stat label={p.kpi.expenses} value={fmt.money(-k.totalExpenses)} tone={k.totalExpenses ? "loss" : undefined} hint={p.kpi.expensesHint} />
            <Stat label={p.kpi.net} value={fmt.money(k.netProfit)} tone={k.netProfit >= 0 ? "gain" : "loss"} hint={p.kpi.netHint} />
            <Stat label={p.kpi.roi} value={roiLabel(roiOf(k.totalPayouts, k.totalExpenses), fmt.pct)} tone={k.totalExpenses ? (k.netProfit >= 0 ? "gain" : "loss") : undefined} hint={p.kpi.roiHint} />
            <Stat label={p.kpi.active} value={k.activeCount} hint={p.kpi.activeHint(activeChallenges, active.length - activeChallenges)} />
            <Stat label={p.kpi.passRate} value={pr.rate === null ? "-" : fmt.pct(pr.rate)} hint={pr.done ? p.kpi.passRateHint(pr.passed, pr.done) : p.kpi.passRateNone} />
          </div>

          <div className="grid xl:grid-cols-12 gap-5">
            <Card className="xl:col-span-8" title={p.cashflow.title} subtitle={p.cashflow.subtitle}>
              <CashflowChart data={monthly} height={280} />
              <div className="flex flex-wrap gap-4 mt-3 text-xs text-muted-foreground">
                <Legend color="bg-pnlPositive" label={t.charts.payouts} />
                <Legend color="bg-pnlNegative" label={t.charts.expenses} />
                <Legend color="bg-primary" label={t.charts.cumulative} line />
              </div>
            </Card>
            <MonthRecap className="xl:col-span-4" accounts={scope} charges={sCharges} />
          </div>

          <div className="grid xl:grid-cols-12 gap-5">
            <Card className="xl:col-span-8" title={p.calendar.title} subtitle={p.calendar.subtitle}>
              <EventCalendar events={events} />
            </Card>
            <div className="xl:col-span-4 flex flex-col gap-5 min-w-0">
              <UpcomingCard charges={sCharges} />
              <ActivityCard events={recent} fill />
            </div>
          </div>

          <PeriodTable accounts={scope} charges={sCharges} />
          {firmRows.length > 0 && <FirmTable rows={firmRows} globalCharges={globalChargesTotal} />}
          <AccountTable accounts={tracked} />

          <div className={`grid gap-5 ${tptWallet ? "xl:grid-cols-2" : ""}`}>
            {tptWallet && <WalletCard wallet={tptWallet} />}
            <ExpenseBreakdown b={allTime} />
          </div>
        </>)}
      </PageBody>

      <PropFirmImportModal open={importOpen} onOpenChange={o => { setImportOpen(o); if (!o) refetchAll(); }} />
      <ExpenseDialog open={expenseOpen} onClose={() => setExpenseOpen(false)} />
    </>
  );
}

